import { apiSlice } from './apiSlice'

/** Mirrors FastAPI `SaleMovingOut` (camelCase aliases) — frmCtrlSalesQryMoving datasheet. */
export interface SaleMoving {
  id: number
  moveNo: string | null
  moveDate: string | null
  warehouseType: string | null
  fromWarehouse: string | null
  loadingDate: string | null
  netWeightKg: number | null
  toWarehouse: string | null
  quality: string | null
  certification: string | null
  packageCode: string | null
  packaging: string | null
  noContTruck: string | null
  truckCompany: string | null
  truckName: string | null
  pic: string | null
  fullName: string | null
  note: string | null
  buyersRef: string | null
  phone: string | null
  picPhone: string | null
  netWeightKgPp: number | null
  noContTruckInt: number | null
  dateUpdate: string | null
  personUpdate: string | null
}

export interface SaleMovingListResponse {
  items: SaleMoving[]
  total: number
  page: number
  pageSize: number
}

export interface SaleMovingListQuery {
  page: number
  pageSize: number
}

/** frmCtrlSalesQryMovingAdd New/Edit payload — camelCase → moving_order_scs. */
export interface SaleMovingWrite {
  moveNo?: string | null
  moveDate?: string | null
  loadingDate?: string | null
  netWeightKg?: number | null
  fromWarehouse?: string | null
  toWarehouse?: string | null
  quality?: string | null
  certification?: string | null
  packageCode?: string | null
  noContTruck?: string | null
  truckCompany?: string | null
  pic?: string | null
  note?: string | null
}

export type SaleMovingForm = Record<string, unknown> & { id: number }

export interface MovingLookupItem { value: string; label: string; meta?: Record<string, unknown> | null }
export interface MovingLookupResponse { items: MovingLookupItem[]; total: number }
export interface SuggestMoveNoResponse { index: number; moveNo: string; exists: boolean }

const saleMovingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleMovings: builder.query<SaleMovingListResponse, SaleMovingListQuery>({
      query: ({ page, pageSize }) => ({ url: '/sale-movings', params: { page, pageSize } }),
      providesTags: ['SaleMoving'],
    }),

    getSaleMoving: builder.query<SaleMoving, number>({
      query: (id) => `/sale-movings/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleMoving', id }],
    }),

    deleteSaleMoving: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-movings/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleMoving'],
    }),

    bulkDeleteSaleMoving: builder.mutation<{ deleted: number }, number[]>({
      query: (ids) => ({ url: '/sale-movings/bulk-delete', method: 'POST', body: { ids } }),
      invalidatesTags: ['SaleMoving'],
    }),

    getSaleMovingCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-movings/access/can-delete',
    }),

    // ── frmCtrlSalesQryMovingAdd ──────────────────────────────────────────
    getSaleMovingForm: builder.query<SaleMovingForm, number>({
      query: (id) => `/sale-movings/${id}/form`,
    }),
    createSaleMoving: builder.mutation<{ moving_order_scs_id: number }, SaleMovingWrite>({
      query: (body) => ({ url: '/sale-movings', method: 'POST', body }),
      invalidatesTags: ['SaleMoving'],
    }),
    updateSaleMoving: builder.mutation<{ moving_order_scs_id: number }, { id: number; body: SaleMovingWrite }>({
      query: ({ id, body }) => ({ url: `/sale-movings/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleMoving'],
    }),
    suggestMoveNo: builder.query<SuggestMoveNoResponse, void>({
      query: () => '/sale-movings/suggest/move-no',
      keepUnusedDataFor: 0,
    }),
    checkMoveNo: builder.query<SuggestMoveNoResponse, { moveNo: string; excludeId?: number }>({
      query: ({ moveNo, excludeId }) => ({
        url: '/sale-movings/check/move-no',
        params: { moveNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    // Lookups
    movingLookupWarehouses: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/warehouses', params: q ? { q } : {} }),
    }),
    movingLookupQualities: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/qualities', params: q ? { q } : {} }),
    }),
    movingLookupCertifications: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/certifications', params: q ? { q } : {} }),
    }),
    movingLookupTruckCompanies: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/truck-companies', params: q ? { q } : {} }),
    }),
    movingLookupPackages: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/packages', params: q ? { q } : {} }),
    }),
    movingLookupPics: builder.query<MovingLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-movings/lookups/pics', params: q ? { q } : {} }),
    }),
    // Print — 1..5 selected records (by move_no) → rpCtrlSalesMoving_Many_N (one dialog).
    getMovingPrintPreview: builder.mutation<{ title: string; html: string; message?: string }, string[]>({
      query: (moveNos) => ({ url: '/sale-movings/print/preview', method: 'POST', body: { moveNos } }),
    }),
  }),
})

export const {
  useGetSaleMovingsQuery,
  useGetSaleMovingQuery,
  useDeleteSaleMovingMutation,
  useBulkDeleteSaleMovingMutation,
  useGetSaleMovingCanHardDeleteQuery,
  useLazyGetSaleMovingFormQuery,
  useCreateSaleMovingMutation,
  useUpdateSaleMovingMutation,
  useLazySuggestMoveNoQuery,
  useLazyCheckMoveNoQuery,
  useMovingLookupWarehousesQuery,
  useMovingLookupQualitiesQuery,
  useMovingLookupCertificationsQuery,
  useMovingLookupTruckCompaniesQuery,
  useMovingLookupPackagesQuery,
  useMovingLookupPicsQuery,
  useGetMovingPrintPreviewMutation,
} = saleMovingApi

export default saleMovingApi
